import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ConfigService } from '../../services/config/config.service';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { ValueHelperService } from '../../services/value-helper/value-helper.service';
import {
    TIMERECORDING_MEANS_OF_TRANSPORT_CODES,
    TIMERECORDING_TYPE_OF_WORK_CODES,
} from '../complex/time-recording-entry.model';
import {
    JsonNotificationIdentifierModel,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
    ODataNotificationIdentifierOutModel,
} from '../low-level/notification-identifier.model';
import { DateModelOptions } from '../options/date-model-options';
import { TimeModelOptions } from '../options/time-model-options';
import { DateModel } from '../primitive/date.model';
import { JsonTextModel, TextModel, TextModelFromJson } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { CompositeModel } from './composite.model';
import { DropdownModel } from './dropdown.model';
import {
    JsonTimesheetAddressModel,
    ODataTimesheetAddressOutModel,
    TimesheetAddressModel,
    TimesheetAddressModelFromJson,
} from './timesheet-address.model';
import { getNextTimesheetEntryCounter } from './timesheet-entry-counter';
import { TimesheetEntryInterface } from './timesheet-entry.interface';

export class TimesheetEntryModel extends CompositeModel implements TimesheetEntryInterface {
    protected clientManager: ClientManagerInterface;
    private readonly configService: ConfigService;

    public startDate: DateModel;
    public startTime: TimeModel;
    public endDate: DateModel;
    public endTime: TimeModel;
    public workCode: TextModel;
    public activityType: DropdownModel<TextModel>;
    public meansOfTransport: DropdownModel<TextModel>;
    public distance: TextModel | undefined;
    public isTravelBackEditable = false;
    public isTravelToEditable = false;
    public travelBackDropdown: DropdownModel<TextModel>;
    private readonly enableStandbyTravel: boolean;

    public readonly index: number;
    public constructor(
        public notificationId: NotificationIdentifierModel,
        startDateTime: moment.Moment,
        endDateTime: moment.Moment,
        workCode: TIMERECORDING_TYPE_OF_WORK_CODES | undefined,
        activityType: string,
        public address: TimesheetAddressModel | undefined,
        private addressInOutModel = true,
        distance: TextModel | undefined,
        meansOfTransport: string,
        public readonly isTravelBillable?: boolean,
        public isAfterHoursCallOut = false,
    ) {
        super('TimesheetEntryModel');

        const injector = CustomInjector.getInjector();
        const valueHelperService = injector.get(ValueHelperService);
        this.clientManager = injector.get(ClientManagerInstanceToken);
        const translate = injector.get(TranslateService);
        this.configService = injector.get(ConfigService);

        this.index = getNextTimesheetEntryCounter();
        this.enableStandbyTravel = this.configService.getEnableStandbyTravel();
        this.isAfterHoursCallOut = isAfterHoursCallOut;

        this.startDate = <DateModel>(
            this.set(new DateModel('StartDate', startDateTime, new DateModelOptions({ readonly: false })))
        );
        this.startTime = <TimeModel>(
            this.set(new TimeModel('StartTime', startDateTime, new TimeModelOptions({ readonly: false })))
        );
        this.endDate = <DateModel>(
            this.set(new DateModel('EndDate', endDateTime, new DateModelOptions({ readonly: false })))
        );
        this.endTime = <TimeModel>(
            this.set(new TimeModel('EndTime', endDateTime, new TimeModelOptions({ readonly: false })))
        );
        this.workCode = <TextModel>this.set(new TextModel('WorkCode', workCode));
        const displayAdditionalTravelInfoForNGMAA =
            this.clientManager.isNgmAA() && this.configService.getDisplayAdditionalTravelInfo();

        if (this.configService.getDisplayMeansOfTransport() || displayAdditionalTravelInfoForNGMAA) {
            const meansOfTransportEntries = valueHelperService
                .getMeansOfTransportation()
                .map((vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.code, vh.code)));
            this.meansOfTransport = new DropdownModel<TextModel>('meansOfTransport', meansOfTransportEntries);
            if (meansOfTransport.length > 0) {
                this.meansOfTransport.selectByPredicate((option) => option.value === meansOfTransport);
            }
        }
        if (displayAdditionalTravelInfoForNGMAA) {
            const destinationEntries = valueHelperService
                .getTravelDestination()
                .map((vh) => new DisplayDecorator<TextModel>(vh.descText, new TextModel(vh.code, vh.code)));

            this.activityType = <DropdownModel<TextModel>>(
                this.set(new DropdownModel<TextModel>('ActivityType', destinationEntries))
            );

            this.activityType.selectByPredicate((destinationEntry) => destinationEntry.model.value === activityType);
        }

        this.distance = distance ? <TextModel>this.set(new TextModel('Distance', distance.value)) : undefined;

        if (
            this.enableStandbyTravel &&
            (workCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY ||
                workCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK)
        ) {
            const travelBackOptions: Array<DisplayDecorator<TextModel>> = [
                new DisplayDecorator<TextModel>(
                    <string>translate.instant('TIMESHEET_TRAVEL_BACK_STANDBY'),
                    new TextModel('TravelEntry', TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY),
                ),
                new DisplayDecorator<TextModel>(
                    <string>translate.instant('TIMESHEET_TRAVEL_BACK'),
                    new TextModel('TravelEntry', TIMERECORDING_TYPE_OF_WORK_CODES.TRBK),
                ),
            ];

            this.travelBackDropdown = <DropdownModel<TextModel>>(
                this.set(new DropdownModel<TextModel>('WorkCode', travelBackOptions))
            );

            this.travelBackDropdown.selectByPredicate((travelType) => travelType.model.value === workCode);
        }
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     */
    public toJSON(): unknown {
        const jsonModel = {
            notificationId: this.notificationId,
            startDate: this.startDate.value,
            startTime: this.startTime.value,
            endDate: this.endDate.value,
            endTime: this.endDate.value,
            workCode: this.workCode.value,
            activityType:
                this.activityType && this.activityType.selectedEntry ? this.activityType.selectedEntry.value : '',
            address: this.address ? this.address : undefined,
            addressInOutModel: this.addressInOutModel,
            distance: this.distance ? this.distance : '',
            meansOfTransport:
                this.meansOfTransport && this.meansOfTransport.selectedEntry
                    ? this.meansOfTransport.selectedEntry.value
                    : '',
            isTravelBillable: this.isTravelBillable,
            afterHourCallOut: this.isAfterHoursCallOut,
        };

        return jsonModel;
    }

    public getOutModel(): ODataTimesheetEntryOutModel {
        const outModel = <ODataTimesheetEntryOutModel>super.getOutModel();
        if (
            this.configService.getDisplayMeansOfTransport() &&
            this.meansOfTransport &&
            this.meansOfTransport.selectedEntry &&
            this.meansOfTransport.selectedEntry.value === TIMERECORDING_MEANS_OF_TRANSPORT_CODES.PASSENGER
        ) {
            if (outModel.WorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO) {
                outModel.WorkCode = TIMERECORDING_TYPE_OF_WORK_CODES.TRTU;
            }
            if (outModel.WorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK) {
                outModel.WorkCode = TIMERECORDING_TYPE_OF_WORK_CODES.TRBU;
            }
        }
        _.merge(outModel, this.notificationId.getOutModel());
        delete (outModel as Omit<ODataTimesheetEntryOutModel, 'FrontId'>).FrontId;

        if (this.addressInOutModel) {
            if (!this.address) {
                throw new Error(`No address set in timesheet entry`);
            }
            _.merge(outModel, this.address.getOutModel());
        }
        if (outModel.WorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.OSR && this.isAfterHoursCallOut) {
            outModel.WorkCode = TIMERECORDING_TYPE_OF_WORK_CODES.OSR_AFTER_HOURS_CALL_OUT;
        }

        return outModel;
    }

    // This function is used to update the travelBack entry's workcode, on change of travelBackDropdown value
    public updateTravelBackWorkcode(): void {
        if (this.travelBackDropdown && this.travelBackDropdown.selectedEntry) {
            this.workCode.value = this.travelBackDropdown.selectedEntry.value;
        }
    }

    public disableTravelBackEntry(disableTravelBackEntryFlag: boolean): void {
        this.travelBackDropdown.options = {
            ...this.travelBackDropdown.options,
            disabled: disableTravelBackEntryFlag,
        };
        this.endDate.options = {
            ...this.endDate.options,
            readonly: disableTravelBackEntryFlag,
        };
        this.endTime.options = {
            ...this.endTime.options,
            readonly: disableTravelBackEntryFlag,
        };
    }
}

export interface ODataTimesheetEntryAaOutModel extends ODataTimesheetEntryOutModel {
    ActivityType: string;
}

export interface ODataTimesheetEntryOutModel
    extends ODataTimesheetAddressOutModel,
        ODataNotificationIdentifierOutModel {
    StartDate: string;
    StartTime: string;
    EndDate: string;
    EndTime: string;
    WorkCode: string;
    Distance: string;
}

export class TimesheetEntryModelFromJson extends TimesheetEntryModel {
    public constructor(data: JsonTimesheetEntryModel) {
        super(
            new NotificationIdentifierModelFromJson(data.notificationId),
            data.startDate ? moment(data.startDate) : moment.invalid(),
            data.endDate ? moment(data.endDate) : moment.invalid(),
            data.workCode ? <TIMERECORDING_TYPE_OF_WORK_CODES>data.workCode : undefined,
            data.activityType,
            data.address ? new TimesheetAddressModelFromJson(data.address) : undefined,
            data.addressInOutModel,
            data.distance ? new TextModelFromJson(data.distance) : undefined,
            data.meansOfTransport,
            data.isTravelBillable,
            data.afterHourCallOut,
        );
    }
}

export interface JsonTimesheetEntryModel {
    notificationId: JsonNotificationIdentifierModel;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    workCode: string;
    activityType: string;
    address: JsonTimesheetAddressModel;
    addressInOutModel: boolean;
    distance: JsonTextModel;
    meansOfTransport: string;
    isTravelBillable: boolean;
    afterHourCallOut: boolean;
}
